import type { LiveUpdatesStack } from 'api/liveUpdates.types'
import palette from 'constants/palette'
import { getRelativeTime } from 'utilities/date'
import { deliveryLockedMode } from '../sections'
import { FLYBUY_PROVIDER_KEY } from '../StatusCard'
import type { OloWebhookPanel } from '../trigger'

export type StatusTone = 'info' | 'amber' | 'green'

// Shared by the banner above the hero and the action block inside the setup
// card, which report the same connection and so must tone identically — kept
// here, beside StatusTone, rather than copied into both.
export const TONE_COLORS: Record<
  StatusTone,
  { background: string; border: string }
> = {
  info: { background: palette.grey10, border: palette.grey20 },
  amber: { background: palette.eggYolk10, border: palette.grey30 },
  green: { background: palette.nephritis10, border: palette.nephritis20 },
}
export type ConnectionRowStatus = 'connect' | 'wait' | 'auto' | 'reconnect'

export type ProviderStatusAction = {
  tone: StatusTone
  titleKey: string
  bodyKey: string
  /** Values for `bodyKey`'s placeholders beyond the shared %{provider}. */
  bodyInterpolation?: { age: string }
  ctaKey?: string
}

export type ProviderStatusContent = {
  headingKey: string
  action: ProviderStatusAction
  connectionStatus: ConnectionRowStatus
  // Whether the stages that reach Thanx only as Olo webhooks — delivery and
  // cancellation (sections.ts `webhookGateFor`) — are gated behind a missing
  // Olo connection.
  oloStagesGated: boolean
  waitingForFirstEvent: boolean
}

export type ProviderStatusKind = 'olo' | 'flybuy' | 'generic'

export const resolveProviderStatusKind = (
  isOloOrdering: boolean,
  isFlybuyProvider: boolean
): ProviderStatusKind => {
  if (isOloOrdering) return 'olo'
  if (isFlybuyProvider) return 'flybuy'
  return 'generic'
}

// Pure derivation of the status section's copy/tone/connection-row content
// from real signals — never a fixed marketing state. Olo is the only
// ordering provider whose order-status connection can be anything other than
// "automatic" (see oloWebhookStatus.ts); every other stack reaches Thanx
// directly.
export const resolveProviderStatusContent = (
  kind: ProviderStatusKind,
  oloWebhook: OloWebhookPanel | undefined
): ProviderStatusContent | undefined => {
  if (kind === 'olo') {
    if (!oloWebhook) return undefined // queries haven't settled yet
    if (oloWebhook.connectionState === 'not_connected') {
      return {
        headingKey: 'olo.not_configured_heading',
        action: {
          tone: 'info',
          titleKey: 'olo.not_configured_action_title',
          bodyKey: 'olo.not_configured_action_body',
          ctaKey: 'olo.not_configured_cta',
        },
        connectionStatus: 'connect',
        oloStagesGated: true,
        waitingForFirstEvent: false,
      }
    }
    if (oloWebhook.connectionState === 'waiting') {
      return {
        headingKey: 'olo.waiting_heading',
        action: {
          tone: 'info',
          titleKey: 'olo.waiting_action_title',
          bodyKey: 'olo.waiting_action_body',
          ctaKey: 'olo.waiting_cta',
        },
        connectionStatus: 'wait',
        oloStagesGated: true,
        waitingForFirstEvent: true,
      }
    }
    if (oloWebhook.needsReconnect) {
      // Same age wording as the Configuration tab's banner (OloWebhookBanner),
      // so the two tabs report the stale connection identically. A stale
      // connection always has a parseable timestamp (isOloWebhookStale), so
      // the ageless copy is only a type-level fallback.
      const { lastReceivedAt } = oloWebhook
      return {
        headingKey: 'olo.needs_attention_heading',
        action: {
          tone: 'amber',
          titleKey: 'olo.needs_attention_action_title',
          ...(lastReceivedAt
            ? {
                bodyKey: 'olo.needs_attention_action_body',
                bodyInterpolation: { age: getRelativeTime(lastReceivedAt) },
              }
            : { bodyKey: 'olo.needs_attention_action_body_no_age' }),
          ctaKey: 'olo.needs_attention_cta',
        },
        connectionStatus: 'reconnect',
        oloStagesGated: false,
        waitingForFirstEvent: false,
      }
    }
    return {
      headingKey: 'olo.connected_heading',
      action: {
        tone: 'green',
        titleKey: 'olo.connected_action_title',
        bodyKey: 'olo.connected_sub',
      },
      connectionStatus: 'auto',
      oloStagesGated: false,
      waitingForFirstEvent: false,
    }
  }

  if (kind === 'flybuy') {
    return {
      headingKey: 'flybuy.heading',
      action: {
        tone: 'green',
        titleKey: 'flybuy.action_title',
        bodyKey: 'flybuy.action_body',
      },
      connectionStatus: 'auto',
      oloStagesGated: false,
      waitingForFirstEvent: false,
    }
  }

  return {
    headingKey: 'generic.heading',
    action: {
      tone: 'green',
      titleKey: 'generic.action_title',
      bodyKey: 'generic.action_body',
    },
    connectionStatus: 'auto',
    oloStagesGated: false,
    waitingForFirstEvent: false,
  }
}

// Whether this configuration still asks the merchant for something. The
// prototype gates the whole setup layer on its action carrying a CTA (v34's
// `c.action && c.action.cta`), which is true in exactly the states the CTA
// names: Olo webhooks to connect, a first event that hasn't arrived yet, or a
// connection to restore. A healthy stack — or one that configures itself,
// like Flybuy — has nothing to ask for, so the section reads as a read-only
// preview instead of a setup checklist.
export const needsSetupAction = (content: ProviderStatusContent): boolean =>
  content.action.ctaKey !== undefined

// Whether a delivery journey exists for this configuration at all, and if so
// whether Thanx can actually report it. Three outcomes, because the setup
// preview treats them differently:
//  - 'available'     something reports delivery, so the journey is previewable
//  - 'not_available' delivery happens, but the merchant runs it themselves, so
//                    no signal ever reaches Thanx (FR-75 'self_managed') — the
//                    journey is shown with its Delivery stage marked
//                    "Not available" rather than pretending it will fire
//  - 'none'          these locations don't deliver — delivery is switched off
//                    ('disabled') or there is no integration at all — so
//                    there is no delivery journey to offer in the first place
//                    (Sketch prototype v41).
// Flybuy is the one exception to the provider check, since it reports the whole
// status ladder itself through `statusProvider` rather than through a delivery
// integration.
export type DeliveryJourney = 'available' | 'not_available' | 'none'

export const deliveryJourney = (
  stack: LiveUpdatesStack,
  deliveryConfigurable: boolean
): DeliveryJourney => {
  const mode = deliveryLockedMode(stack, deliveryConfigurable)
  if (mode === 'self_managed') return 'not_available'
  if (mode === 'disabled') return 'none'
  if (stack.statusProvider?.key === FLYBUY_PROVIDER_KEY) return 'available'
  return stack.deliveryProvider ? 'available' : 'none'
}
